import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface CookieConsent {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

interface CookieConsentContextType {
  consent: CookieConsent | null
  showBanner: boolean
  acceptAll: () => void
  rejectAll: () => void
  acceptSelected: (consent: CookieConsent) => void
  resetConsent: () => void
  openSettings: () => void
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

const COOKIE_CONSENT_KEY = 'bollettaamica_cookie_consent'

// Generate a simple anonymous ID for tracking preferences
function getAnonymousId(): string {
  let id = localStorage.getItem('bollettaamica_anonymous_id')
  if (!id) {
    id = 'anon_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
    localStorage.setItem('bollettaamica_anonymous_id', id)
  }
  return id
}

// Save consent to backend
async function saveConsentToBackend(consent: CookieConsent) {
  try {
    const anonymousId = getAnonymousId()
    await fetch('/api/cookie-preferences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anonymousId,
        consent,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      }),
    })
  } catch (error) {
    // Silently fail - this is non-critical
    console.warn('Failed to save cookie preferences to backend:', error)
  }
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(null)
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored) {
      setConsent(JSON.parse(stored))
      setShowBanner(false)
    } else {
      setShowBanner(true)
    }
  }, [])

  const saveConsent = (newConsent: CookieConsent) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent))
    setConsent(newConsent)
    setShowBanner(false)
    // Save to backend
    saveConsentToBackend(newConsent)
  }

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    })
  }

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    })
  }

  const acceptSelected = (selectedConsent: CookieConsent) => {
    saveConsent({
      ...selectedConsent,
      necessary: true, // Always required
    })
  }

  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY)
    setConsent(null)
    setShowBanner(true)
  }

  const openSettings = () => {
    setShowBanner(true)
  }

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        acceptAll,
        rejectAll,
        acceptSelected,
        resetConsent,
        openSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}
